import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

interface StripePaymentFormProps {
  amount: number;
  packageName: string;
  customerEmail: string;
  onSuccess: () => void;
  onBack: () => void;
}

export default function StripePaymentForm({
  amount,
  packageName,
  customerEmail,
  onSuccess,
  onBack,
}: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/booking?success=true`,
        receipt_email: customerEmail,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'An error occurred during payment');
      setIsProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-green-50 rounded-2xl p-6 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-semibold text-neutral-700">Package:</span>
          <span className="text-sm text-neutral-800">{packageName}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-neutral-700">Total Amount:</span>
          <span className="font-display text-2xl font-bold text-amber-700">
            ${amount.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="bg-neutral-50 rounded-2xl p-6">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'cashapp'],
          }}
        />
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700 text-center">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex-1 py-4 border-2 border-neutral-300 text-neutral-700 font-bold rounded-full hover:bg-neutral-50 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Back
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 py-4 bg-gradient-to-r from-amber-700 to-amber-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Processing...
            </span>
          ) : (
            `Pay $${amount.toLocaleString()}`
          )}
        </button>
      </div>

      <div className="text-center">
        <p className="text-xs text-neutral-500">
          <i className="ri-lock-line mr-1"></i>
          Secure payment powered by Stripe
        </p>
      </div>
    </form>
  );
}